import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  Funcionario, InsertFuncionario,
  Atividade, InsertAtividade,
  Quinzena, InsertQuinzena,
  Diaria, InsertDiaria,
  Tarefa, InsertTarefa,
  FuncionarioSetor,
} from "@shared/schema";
import { intervaloDaCompetencia, type LinhaMensalistaMes } from "@shared/equipe-fechamento";

/**
 * Financeiro v3 — a queryKey da aba "Mensalistas · mês". Toda mutação que muda
 * uma linha do mês (adiantamento, dispensa, fechamento, lançamento, fechar
 * quinzena — a cascata PREVISTO→PAGO —, tarefa/presença) invalida esta chave.
 */
export const MENSALISTAS_COMPETENCIA_KEY = "/api/equipe/mensalistas/competencia";

// ─── Funcionários ─────────────────────────────────────────────────────────────

export function useFuncionarios(includeInativos = false) {
  const url = includeInativos ? "/api/equipe/funcionarios?includeInativos=true" : "/api/equipe/funcionarios";
  return useQuery<Funcionario[]>({ queryKey: ["/api/equipe/funcionarios", includeInativos], queryFn: () => fetchJson<Funcionario[]>(url) });
}

export function useCreateFuncionario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertFuncionario>) =>
      apiRequest("POST", "/api/equipe/funcionarios", data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/funcionarios"] }),
  });
}

export function useUpdateFuncionario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsertFuncionario> }) =>
      apiRequest("PATCH", `/api/equipe/funcionarios/${id}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/funcionarios"] }),
  });
}

export function useDeleteFuncionario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/equipe/funcionarios/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/funcionarios"] }),
  });
}

// ─── Vínculo funcionário ↔ setor (encarregado, vigência datada) ──────────────

/**
 * Sem filtro → só os vínculos ATIVOS da org. É o que a lista de funcionários usa
 * para derivar o rótulo "Encarregado" (mensalista com ≥1 vínculo ativo) sem uma
 * request por funcionário.
 */
export function useVinculosAtivos() {
  return useQuery<FuncionarioSetor[]>({
    queryKey: ["/api/equipe/funcionario-setor"],
    queryFn: () => fetchJson<FuncionarioSetor[]>("/api/equipe/funcionario-setor"),
  });
}

/** Histórico completo (ativos + encerrados) de um funcionário. */
export function useVinculosDoFuncionario(funcionarioId: string | undefined) {
  return useQuery<FuncionarioSetor[]>({
    queryKey: ["/api/equipe/funcionario-setor", "funcionario", funcionarioId],
    queryFn: () => fetchJson<FuncionarioSetor[]>(`/api/equipe/funcionario-setor?funcionarioId=${funcionarioId}`),
    enabled: !!funcionarioId,
  });
}

// ⚠️ Não existem hooks de MUTAÇÃO de vínculo aqui, de propósito (I15).
//
// Criar/corrigir/encerrar vínculo passa pelo salvamento do FUNCIONÁRIO
// (`vinculosSetor` no payload de POST/PATCH /api/equipe/funcionarios), numa
// transação só. Um segundo caminho de escrita a partir da tela foi exatamente o
// bug: gravar o vínculo na hora do "Vincular" fazia o servidor validar o regime
// PERSISTIDO, então virar Encarregado direto de Diarista era impossível — e o
// Cancelar não desfazia nada, porque já estava no banco.
//
// As rotas diretas `POST/PATCH /api/equipe/funcionario-setor` continuam existindo
// para uso via API (e são cobertas por testes), mas a UI não as chama.

// ─── Atividades ───────────────────────────────────────────────────────────────

export function useAtividades() {
  return useQuery<Atividade[]>({ queryKey: ["/api/equipe/atividades"] });
}

export function useCreateAtividade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertAtividade>) =>
      apiRequest("POST", "/api/equipe/atividades", data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/atividades"] }),
  });
}

export function useUpdateAtividade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsertAtividade> }) =>
      apiRequest("PATCH", `/api/equipe/atividades/${id}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/atividades"] }),
  });
}

// ─── Quinzenas ────────────────────────────────────────────────────────────────

export function useQuinzenas() {
  return useQuery<Quinzena[]>({ queryKey: ["/api/equipe/quinzenas"] });
}

export function useQuinzenaAtiva() {
  return useQuery<Quinzena | null>({
    queryKey: ["/api/equipe/quinzenas/ativa"],
    retry: false,
  });
}

export function useCreateQuinzena() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertQuinzena>) =>
      apiRequest("POST", "/api/equipe/quinzenas", data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas/ativa"] });
    },
  });
}

export function useFecharQuinzena() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/equipe/quinzenas/${id}/fechar`, {}).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas/ativa"] });
      // A cascata PREVISTO→PAGO (D13) e o re-sync do snapshot mudam a linha do mês.
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
    },
  });
}

export function usePagarQuinzena() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiRequest("POST", `/api/equipe/quinzenas/${id}/pagar`, {}).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] }),
  });
}

export function useResumoQuinzena(quinzenaId: string | undefined) {
  return useQuery({
    queryKey: ["/api/equipe/quinzenas", quinzenaId, "resumo"],
    queryFn: () => fetchJson(`/api/equipe/quinzenas/${quinzenaId}/resumo`),
    enabled: !!quinzenaId,
  });
}

// ─── Diárias ──────────────────────────────────────────────────────────────────

export function useDiariasByQuinzena(quinzenaId: string | undefined) {
  return useQuery<Diaria[]>({
    queryKey: ["/api/equipe/diarias", quinzenaId],
    queryFn: () => fetchJson<Diaria[]>(`/api/equipe/diarias?quinzenaId=${quinzenaId}`),
    enabled: !!quinzenaId,
  });
}

export function useCreateDiaria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertDiaria>) =>
      apiRequest("POST", "/api/equipe/diarias", data).then(r => r.json()),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/diarias", (vars as any).quinzenaId] });
    },
  });
}

export function useDeleteDiaria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quinzenaId }: { id: string; quinzenaId: string }) =>
      apiRequest("DELETE", `/api/equipe/diarias/${id}`).then(() => quinzenaId),
    onSuccess: (_quinzenaId) => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/diarias"] });
    },
  });
}

export function useUpdateDiaria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tipo, valor }: { id: string; tipo?: string; valor?: string }) =>
      apiRequest("PATCH", `/api/equipe/diarias/${id}`, { tipo, valor }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/diarias"] }),
  });
}

// ─── Tarefas ──────────────────────────────────────────────────────────────────

export function useTarefasByData(data: string) {
  return useQuery<Tarefa[]>({
    queryKey: ["/api/equipe/tarefas", data],
    queryFn: () => fetchJson<Tarefa[]>(`/api/equipe/tarefas?data=${data}`),
    enabled: !!data,
  });
}

export function useTarefasByPeriodo(dataInicio: string, dataFim: string) {
  return useQuery<Tarefa[]>({
    queryKey: ["/api/equipe/tarefas", dataInicio, dataFim],
    queryFn: () => fetchJson<Tarefa[]>(`/api/equipe/tarefas?dataInicio=${dataInicio}&dataFim=${dataFim}`),
    enabled: !!dataInicio && !!dataFim,
  });
}

// §3 — a falha do consolidador não é mais um 200 mudo: o servidor devolve
// `diariaConsolidada: false` + `avisoDiaria` no corpo, e os três hooks abaixo
// mostram isso na hora, não só depois de alguém notar numa auditoria.
/**
 * §3 — a falha do consolidador chega ao usuário como toast, na hora. Exportado
 * porque o sheet do dia (TarefaDiariaSheet) tem mutations próprias que também
 * precisam ler `diariaConsolidada` da resposta — antes elas descartavam o corpo.
 */
export function avisarSeDiariaFalhou(toast: ReturnType<typeof useToast>["toast"], data: unknown): void {
  const d = data as { diariaConsolidada?: boolean; avisoDiaria?: string } | null | undefined;
  if (d && d.diariaConsolidada === false) {
    toast({
      title: "Tarefa salva, mas a diária não foi calculada",
      description: d.avisoDiaria ?? "Tente editar a tarefa de novo em alguns minutos.",
      variant: "destructive",
    });
  }
}

export function useCreateTarefa() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: Partial<InsertTarefa>) =>
      apiRequest("POST", "/api/equipe/tarefas", data).then(r => r.json()),
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
      // Presença do mensalista na aba Mês vem das tarefas/diárias do mês civil.
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
      avisarSeDiariaFalhou(toast, data);
    },
  });
}

export function useUpdateTarefa() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsertTarefa> }) =>
      apiRequest("PATCH", `/api/equipe/tarefas/${id}`, data).then(r => r.json()),
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
      // Presença do mensalista na aba Mês vem das tarefas/diárias do mês civil.
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
      avisarSeDiariaFalhou(toast, data);
    },
  });
}

export function useDeleteTarefa() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    // DELETE agora só tem corpo (200) quando o consolidador falhou (§3) — sucesso
    // pleno continua 204 sem corpo, `r.json()` estouraria nesse caso.
    mutationFn: async (id: string) => {
      const r = await apiRequest("DELETE", `/api/equipe/tarefas/${id}`);
      return r.status === 204 ? null : r.json();
    },
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
      // Presença do mensalista na aba Mês vem das tarefas/diárias do mês civil.
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
      avisarSeDiariaFalhou(toast, data);
    },
  });
}

// ─── Fase 2: adiantamento quinzenal e fechamento mensal ──────────────────────
//
// ⚠️ Nenhum destes toca a folha do diarista. O bloco de mensalistas vem de
// endpoint PRÓPRIO (§4.5): se um `MENSALISTA` for gravado com valor por engano,
// ele não tem caminho até o total pago da quinzena.

export interface MensalistaDaQuinzena {
  id: string;
  nome: string;
  apelido: string | null;
  tipo_remuneracao: string;
  salario_mensal: string | null;
  forma_pagamento: string | null;
  adiantamento_pct: string | null;
  variavel_max_mensal: string | null;
  valor_kg_colheita: string | null;
  dias_registro: number;
  dias_falta: number;
  datas_registro: string[];
  datas_falta: string[];
  // ── Adiantamento DESTA quinzena — o CAIXA (soma no "Sai nesta quinzena"). ──
  adiantamento_id: string | null;
  adiantamento_valor: string | null;
  adiantamento_status: string | null;
  adiantamento_competencia: string | null;
  // ── Adiantamento da COMPETÊNCIA desta quinzena (qualquer quinzena do mês) ──
  // Responde "precisa gerar?", nunca "quanto sai aqui": um mês pode ter DUAS
  // quinzenas, e o adiantamento pago na primeira ficava invisível na segunda.
  // NUNCA some `adiantamento_comp_valor` no caixa — ver
  // `temAdiantamentoNaCompetencia` / `adiantamentoSaiNestaQuinzena`.
  adiantamento_comp_id: string | null;
  adiantamento_comp_valor: string | null;
  adiantamento_comp_status: string | null;
  adiantamento_comp_origem: string | null;
  adiantamento_comp_quinzena_id: string | null;
  adiantamento_comp_quinzena_inicio: string | null;
  adiantamento_comp_quinzena_fim: string | null;
  adiantamento_comp_quinzena_data_pagamento: string | null;
  /** "Não houve adiantamento nesta competência", declarado (migração 046). */
  dispensa_comp_id: string | null;
  dispensa_comp_motivo: string | null;
  /** §4 — tem diária MENSALISTA nesta quinzena, mas o regime de hoje já não é mais esse. */
  era_mensalista: boolean;
}

/** Bloco 2 do Financeiro — mensalistas COM dado nesta quinzena. */
export function useMensalistasDaQuinzena(quinzenaId: string | undefined) {
  return useQuery<MensalistaDaQuinzena[]>({
    queryKey: ["/api/equipe/quinzenas", quinzenaId, "mensalistas"],
    queryFn: () => fetchJson<MensalistaDaQuinzena[]>(`/api/equipe/quinzenas/${quinzenaId}/mensalistas`),
    enabled: !!quinzenaId,
  });
}

export function useDefinirDataPagamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dataPagamento }: { id: string; dataPagamento: string | null }) =>
      apiRequest("PATCH", `/api/equipe/quinzenas/${id}`, { dataPagamento }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas/ativa"] });
    },
  });
}

export interface CandidatoAdiantamento {
  funcionarioId: string;
  nome: string;
  apelido: string | null;
  salarioMensal: string | null;
  adiantamentoPct: string | null;
  valorSugerido: string;
}

/**
 * Candidatos ao adiantamento + valor sugerido pelo SERVIDOR. Já sai filtrado por
 * I13 (só quem recebe quinzenal) e sem quem já tem adiantamento no mês.
 * `retry: false` porque o 409 SEM_DATA_PAGAMENTO é resposta esperada, não falha.
 */
export function useCandidatosAdiantamento(quinzenaId: string | undefined, habilitado: boolean) {
  return useQuery<{ competencia: string; dataPagamento: string; candidatos: CandidatoAdiantamento[] }>({
    queryKey: ["/api/equipe/quinzenas", quinzenaId, "adiantamentos", "candidatos"],
    queryFn: () => fetchJson(`/api/equipe/quinzenas/${quinzenaId}/adiantamentos/candidatos`),
    enabled: !!quinzenaId && habilitado,
    retry: false,
  });
}

export function useGerarAdiantamentos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ quinzenaId, competencia, itens }: {
      quinzenaId: string;
      competencia: string;
      itens: Array<{ funcionarioId: string; valor: string }>;
    }) =>
      apiRequest("POST", `/api/equipe/quinzenas/${quinzenaId}/adiantamentos/gerar`, { competencia, itens })
        .then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos/pendencias"] });
    },
  });
}

export interface FechamentoDoMes {
  id: string;
  funcionarioId: string;
  competencia: string;
  nome: string;
  apelido: string | null;
  salarioFixo: string | null;
  formaPagamento: string | null;
  adiantamentosPagos: string | null;
  variavelApurada: string | null;
  variavelObs: string | null;
  variavelMaxMensal: string | null;
  diasRegistro: number | null;
  diasFalta: number | null;
  descontos: string | null;
  descontosObs: string | null;
  liquidoFinal: string | null;
  status: string;
}

export function useFechamentos(competencia: string | undefined) {
  return useQuery<FechamentoDoMes[]>({
    queryKey: ["/api/equipe/fechamentos", competencia],
    queryFn: () => fetchJson<FechamentoDoMes[]>(`/api/equipe/fechamentos?competencia=${competencia}`),
    enabled: !!competencia,
  });
}

export function useGerarFechamentos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (competencia: string) =>
      apiRequest("POST", "/api/equipe/fechamentos/gerar", { competencia }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
    },
  });
}

export function useAtualizarFechamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/equipe/fechamentos/${id}`, data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
    },
  });
}

// ─── Pendência de adiantamento na competência (migração 046) ─────────────────

export interface PendenciaAdiantamentoDoMes {
  funcionarioId: string;
  nome: string;
  apelido: string | null;
  salarioMensal: string;
  adiantamentoPct: string;
  valorSugerido: string;
}

/**
 * Quem vai receber o salário CHEIO se ninguém fizer nada. É a lista que trava o
 * fechamento mensal — foi ela, invisível em agosto, que deixou o mês pagar
 * R$ 2.081,00 a quem já tinha recebido R$ 1.040,50 por fora.
 */
export function usePendenciasFechamento(competencia: string | undefined) {
  return useQuery<{ competencia: string; quinzenaAberta: boolean; pendencias: PendenciaAdiantamentoDoMes[] }>({
    queryKey: ["/api/equipe/fechamentos/pendencias", competencia],
    queryFn: () => fetchJson(`/api/equipe/fechamentos/pendencias?competencia=${competencia}`),
    enabled: !!competencia,
  });
}

/** Registra um adiantamento JÁ PAGO fora do sistema (aceita quinzena FECHADA). */
export function useRegistrarAdiantamentoExterno() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      funcionarioId: string; competencia: string; valor: string; pagoEm: string; observacao: string;
    }) => apiRequest("POST", "/api/equipe/adiantamentos/externo", data).then(r => r.json()),
    // O servidor re-sincroniza o snapshot dos fechamentos ABERTOS na mesma
    // chamada; invalidar os dois é o que faz a prévia cair na hora.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos/pendencias"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
    },
  });
}

/** "Conferi: não houve adiantamento nesta competência." */
export function useDispensarAdiantamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { funcionarioId: string; competencia: string; motivo: string }) =>
      apiRequest("POST", "/api/equipe/adiantamentos/dispensa", data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos/pendencias"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
    },
  });
}

export interface LancamentoCriado {
  ok: true;
  quinzenaId: string;
  /** Só no desconto de mensalista (D-M5): o mês civil da data. */
  competencia?: string;
  /** `FECHAMENTO_JA_GERADO` — o fechamento ABERTO do mês já existe e o campo Descontos dele não é re-semeado. */
  aviso?: string;
}

/**
 * Lançamento avulso com sinal — ADICIONAL (+) ou DESCONTO (−). Nunca vira diária.
 *
 * `competenciaAlvo` (Financeiro v3) é validação SÓ DO CLIENT: a aba Mês só
 * lança dentro do mês em exibição, e um erro claro aqui é melhor do que um
 * lançamento que some da tela porque caiu noutra competência. O servidor
 * não recebe esse campo.
 */
export function useCriarLancamento() {
  const qc = useQueryClient();
  return useMutation<LancamentoCriado, Error, {
    funcionarioId: string; data: string; sinal: "ADICIONAL" | "DESCONTO";
    descricao: string; valor: string; competenciaAlvo?: string;
  }>({
    mutationFn: async ({ competenciaAlvo, ...data }) => {
      if (competenciaAlvo) {
        const { inicio, fim } = intervaloDaCompetencia(competenciaAlvo);
        if (data.data < inicio || data.data > fim) {
          throw new Error(`400: {"error":"A data precisa estar dentro de ${competenciaAlvo}."}`);
        }
      }
      const r = await apiRequest("POST", "/api/equipe/lancamentos", data);
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
    },
  });
}

/** Config da org — hoje só o dia do fechamento mensal. */
export function useOrganizacao() {
  return useQuery<{ id: string; nome: string; diaPagamentoMensal: number | null }>({
    queryKey: ["/api/organizations/me"],
  });
}

// ─── Financeiro v3.1: aba "Mensalistas · mês" ────────────────────────────────

export interface QuinzenaDaCompetencia {
  id: string;
  status: string;
  periodoInicio: string;
  periodoFim: string;
  dataPagamento: string | null;
}

export interface MensalistasDaCompetencia {
  competencia: string;
  inicio: string;
  fim: string;
  /** Dia em que o fechamento desta competência é pago (mês seguinte, `dia_pagamento_mensal`). */
  dataPagamento: string;
  /** A primeira quinzena que começa no mês — a que carrega o adiantamento. */
  quinzenaDaCompetencia: QuinzenaDaCompetencia | null;
  /** Todas as que começam no mês — "Gerar fechamento" só libera com nenhuma ABERTA. */
  quinzenasDaCompetencia: QuinzenaDaCompetencia[];
  linhas: LinhaMensalistaMes[];
}

export type { LinhaMensalistaMes };

/** Uma linha por mensalista na competência (mês civil). Carregar RECONCILIA os adiantamentos no servidor. */
export function useMensalistasDaCompetencia(competencia: string | undefined) {
  return useQuery<MensalistasDaCompetencia>({
    queryKey: [MENSALISTAS_COMPETENCIA_KEY, competencia],
    queryFn: () => fetchJson<MensalistasDaCompetencia>(`${MENSALISTAS_COMPETENCIA_KEY}?competencia=${competencia}`),
    enabled: !!competencia,
  });
}

// ─── Fase 3: pagamento adicional na tarefa ────────────────────────────────────
//
// ⚠️ NÃO é diária. Endpoints e hooks SEPARADOS de propósito — mesmo padrão do
// bloco de mensalistas (§4.5 da Fase 2): nenhum caminho implícito até o total
// de `diarias`.

export interface TarefaAdicionalRow {
  id: string;
  tarefaId: string;
  funcionarioId: string;
  quinzenaId: string;
  data: string;
  tipo: "COLHEITA" | "OUTRO";
  descricao: string | null;
  kg: string | null;
  valorKg: string | null;
  valor: string;
}

export function useAdicionaisDaTarefa(tarefaId: string | undefined) {
  return useQuery<TarefaAdicionalRow[]>({
    queryKey: ["/api/equipe/tarefas", tarefaId, "adicionais"],
    queryFn: () => fetchJson<TarefaAdicionalRow[]>(`/api/equipe/tarefas/${tarefaId}/adicionais`),
    enabled: !!tarefaId,
  });
}

export function useCriarAdicionais() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tarefaId, tipo, descricao, itens }: {
      tarefaId: string;
      tipo: "COLHEITA" | "OUTRO";
      descricao?: string;
      itens: Array<{ funcionarioId: string; kg?: string; valorKg?: string; valor?: string }>;
    }) =>
      apiRequest("POST", `/api/equipe/tarefas/${tarefaId}/adicionais`, { tipo, descricao, itens })
        .then(r => r.json()),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas", vars.tarefaId, "adicionais"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
    },
  });
}

export function useAtualizarAdicional() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/equipe/adicionais/${id}`, data).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
    },
  });
}

export function useExcluirAdicional() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/equipe/adicionais/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: [MENSALISTAS_COMPETENCIA_KEY] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
    },
  });
}

export interface AdicionalAgregado {
  funcionario_id: string;
  /** Regime DE HOJE do funcionário — D-M4: desconto de MENSALISTA não sai no caixa da quinzena. */
  tipo_remuneracao?: string | null;
  // §2.2 — agrupado por (funcionario_id, tipo): nunca mistura COLHEITA com
  // OUTRO na mesma linha (o R$/kg médio ficaria sem sentido, ou dividiria por
  // zero se a pessoa só tivesse OUTRO).
  tipo: "COLHEITA" | "OUTRO";
  /** ADICIONAL (+) | DESCONTO (−) — migração 046. `total` é sempre positivo. */
  sinal: "ADICIONAL" | "DESCONTO";
  qtd: number;
  total: string;
  kg_total: string;
}

export interface AdicionalDetalhe {
  id: string;
  tarefaId: string;
  funcionarioId: string;
  nome: string;
  apelido: string | null;
  tipo_remuneracao?: string | null;
  sinal?: "ADICIONAL" | "DESCONTO" | null;
  data: string;
  tipo: "COLHEITA" | "OUTRO";
  descricao: string | null;
  kg: string | null;
  valorKg: string | null;
  valor: string;
}

/** Alimenta o Financeiro: agregado por pessoa + detalhe por lançamento. */
export function useAdicionaisDaQuinzena(quinzenaId: string | undefined) {
  return useQuery<{ agregado: AdicionalAgregado[]; detalhe: AdicionalDetalhe[] }>({
    queryKey: ["/api/equipe/quinzenas", quinzenaId, "adicionais"],
    queryFn: () => fetchJson(`/api/equipe/quinzenas/${quinzenaId}/adicionais`),
    enabled: !!quinzenaId,
  });
}

export interface AdicionalAgregadoCompetencia {
  funcionario_id: string;
  qtd: number;
  /** Só ADICIONAL — desconto tem query própria (`getDescontosAgregadosPorCompetencia`). */
  total: string;
  kg_total: string;
}

/**
 * Agregado de adicionais por COMPETÊNCIA (mês) — atravessa duas quinzenas, ao
 * contrário de `useAdicionaisDaQuinzena`. Alimenta o aviso de estouro de teto
 * no fechamento mensal (§3) e no bloco de lançamento.
 */
export function useAdicionaisPorCompetencia(competencia: string | undefined) {
  return useQuery<AdicionalAgregadoCompetencia[]>({
    queryKey: ["/api/equipe/fechamentos/adicionais", competencia],
    queryFn: () => fetchJson(`/api/equipe/fechamentos/adicionais?competencia=${competencia}`),
    enabled: !!competencia,
  });
}

export interface AuxilioAgregadoCompetencia {
  funcionario_id: string;
  qtd: number;
  total: string;
}

/** Auxílios (tipo=AUXILIO) da competência, por funcionário — separado de adicionais de propósito (ver rota). */
export function useAuxiliosPorCompetencia(competencia: string | undefined) {
  return useQuery<AuxilioAgregadoCompetencia[]>({
    queryKey: ["/api/equipe/fechamentos/auxilios", competencia],
    queryFn: () => fetchJson(`/api/equipe/fechamentos/auxilios?competencia=${competencia}`),
    enabled: !!competencia,
  });
}
